import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { AddableReachability, Forwarding, Notification } from './notifications-forwardings.model';
import { environment } from '../../../environments/environment';
/** HAL response with _embedded */
interface HalResponse {
  _embedded?: Record<string, unknown[]>;
}

/** Unwrap HAL _embedded list by key */
function unwrapHal<T>(key: string): (source: Observable<HalResponse>) => Observable<T[]> {
  return (source) => source.pipe(
    map((res: HalResponse) => (res?._embedded?.[key] ?? []) as T[]),
  );
}

@Injectable({ providedIn: 'root' })
export class NotificationsForwardingsService {
  private readonly http = inject(HttpClient);

  private psBase(psId: number): string {
    return `${environment.phoneServiceUrl}/phoneservices/${psId}`;
  }

  // ========== §1 Call Notifications (PS-scoped) ==========

  getCallNotifications(psId: number): Observable<Notification[]> {
    return this.http.get<HalResponse>(`${this.psBase(psId)}/notifications`).pipe(
      unwrapHal('notificationDataList'),
    );
  }

  getAddableCallNotificationReachabilities(psId: number): Observable<AddableReachability[]> {
    return this.http.get<HalResponse>(`${this.psBase(psId)}/notifications/reachabilities/addable`).pipe(
      unwrapHal('reachabilityDataList'),
    );
  }

  addCallNotification(psId: number, reachabilityId: number): Observable<Notification> {
    return this.http.post<Notification>(`${this.psBase(psId)}/notifications`, {
      reachability: { reachabilityId },
    });
  }

  deleteCallNotification(psId: number, notificationId: number): Observable<void> {
    return this.http.delete<void>(`${this.psBase(psId)}/notifications/${notificationId}`);
  }

  // ========== §2 Manual Forwardings (PS-scoped) ==========

  getManualForwardings(psId: number): Observable<Forwarding[]> {
    return this.http.get<HalResponse>(`${this.psBase(psId)}/forwardings/manual`).pipe(
      unwrapHal('forwardingManualDataList'),
    );
  }

  getAddableManualReachabilities(psId: number): Observable<AddableReachability[]> {
    return this.http.get<HalResponse>(`${this.psBase(psId)}/forwardings/manual/reachabilities/addable`).pipe(
      unwrapHal('reachabilityDataList'),
    );
  }

  addManualForwarding(psId: number, reachabilityId: number, order: number): Observable<Forwarding> {
    return this.http.post<Forwarding>(`${this.psBase(psId)}/forwardings/manual`, {
      reachability: { reachabilityId },
      order,
    });
  }

  updateManualForwarding(psId: number, forwardingId: number, payload: Partial<Forwarding>): Observable<Forwarding> {
    return this.http.put<Forwarding>(`${this.psBase(psId)}/forwardings/manual/${forwardingId}`, payload);
  }

  deleteManualForwarding(psId: number, forwardingId: number): Observable<void> {
    return this.http.delete<void>(`${this.psBase(psId)}/forwardings/manual/${forwardingId}`);
  }

  // ========== §3 Busy Forwardings (PS-scoped) ==========

  getBusyForwardings(psId: number): Observable<Forwarding[]> {
    return this.http.get<HalResponse>(`${this.psBase(psId)}/forwardings/busy`).pipe(
      unwrapHal('forwardingBusyDataList'),
    );
  }

  getAddableBusyReachabilities(psId: number): Observable<AddableReachability[]> {
    return this.http.get<HalResponse>(`${this.psBase(psId)}/forwardings/busy/reachabilities/addable`).pipe(
      unwrapHal('reachabilityDataList'),
    );
  }

  addBusyForwarding(psId: number, reachabilityId: number, order: number): Observable<Forwarding> {
    return this.http.post<Forwarding>(`${this.psBase(psId)}/forwardings/busy`, {
      reachability: { reachabilityId },
      order,
    });
  }

  updateBusyForwarding(psId: number, forwardingId: number, payload: Partial<Forwarding>): Observable<Forwarding> {
    return this.http.put<Forwarding>(`${this.psBase(psId)}/forwardings/busy/${forwardingId}`, payload);
  }

  deleteBusyForwarding(psId: number, forwardingId: number): Observable<void> {
    return this.http.delete<void>(`${this.psBase(psId)}/forwardings/busy/${forwardingId}`);
  }

  // ========== §4 Direct Forwardings (per CC number, PS-scoped) ==========

  getDirectForwardings(psId: number, number: string): Observable<Forwarding[]> {
    return this.http.get<HalResponse>(`${this.psBase(psId)}/numbers/${number}/forwardings/direct`).pipe(
      unwrapHal('forwardingDirectDataList'),
    );
  }

  getAddableDirectReachabilities(psId: number, number: string): Observable<AddableReachability[]> {
    // This endpoint returns a plain JSON array, not HAL wrapped
    return this.http.get<AddableReachability[]>(`${this.psBase(psId)}/numbers/${number}/forwardings/direct/reachabilities/addable`);
  }

  addDirectForwarding(psId: number, number: string, reachabilityId: number, order: number): Observable<Forwarding> {
    return this.http.post<Forwarding>(`${this.psBase(psId)}/numbers/${number}/forwardings/direct`, {
      reachability: { reachabilityId },
      order,
    });
  }

  updateDirectForwarding(psId: number, number: string, forwardingId: number, payload: Partial<Forwarding>): Observable<Forwarding> {
    return this.http.put<Forwarding>(`${this.psBase(psId)}/numbers/${number}/forwardings/direct/${forwardingId}`, payload);
  }

  deleteDirectForwarding(psId: number, number: string, forwardingId: number): Observable<void> {
    return this.http.delete<void>(`${this.psBase(psId)}/numbers/${number}/forwardings/direct/${forwardingId}`);
  }

  // ========== §5 AM Notifications (UA-scoped) ==========

  getAmNotifications(userAccountId: number): Observable<Notification[]> {
    return this.http.get<HalResponse>(`${environment.phoneServiceUrl}/voicebox/${userAccountId}/notifications`).pipe(
      unwrapHal('notificationDataList'),
    );
  }

  getAddableAmReachabilities(userAccountId: number): Observable<AddableReachability[]> {
    return this.http.get<HalResponse>(`${environment.phoneServiceUrl}/voicebox/${userAccountId}/notifications/reachabilities/addable`).pipe(
      unwrapHal('reachabilityDataList'),
    );
  }

  addAmNotification(userAccountId: number, reachabilityId: number): Observable<Notification> {
    return this.http.post<Notification>(`${environment.phoneServiceUrl}/voicebox/${userAccountId}/notifications`, {
      reachability: { reachabilityId },
    });
  }

  deleteAmNotification(userAccountId: number, notificationId: number): Observable<void> {
    return this.http.delete<void>(`${environment.phoneServiceUrl}/voicebox/${userAccountId}/notifications/${notificationId}`);
  }
}
